(ns io.daskul.tutorial.webapp.views.inbox
  "Recency inbox (SMS-36) — the 'use' half of the Facebook App Review demo.

   Lists conversations newest-first (the list API already defaults to
   `last-message-at desc`) as Option-B rows: initials avatar + channel badge,
   sender name, relative timestamp, and a \"You:\"-prefixed last-message
   preview for outbound replies. Tapping a row opens the existing
   conversation_thread view. An inbound message surfaces as a new top row via
   recency; an admin reply bumps that row back to the top.

   Visual contract: pipeline-compare.html Option B (.b-item / .b-avatar /
   .b-channel / .b-top / .b-preview / .b-unread). Lead pills, tabs, and
   empty-state are deferred to SMS-15 — this view stays recency-pure.
   Unread state (bold name + trailing dot when unread-count > 0) and
   list polling land with SMS-29.

   Pure derivations (relative-time, \"You:\" preview, initials) live in the
   shared lib ns io.daskul.webapp.inbox and are unit-tested there."
  (:require
   [uix.core :refer [defui $ use-effect]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.inbox :as inbox]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.tutorial.webapp.router :as router]))

;; ============================================================================
;; Row (Option B) — pure layout
;; ============================================================================

(defui conversation-avatar
  "Initials circle with a Facebook channel badge, per the mockup .b-avatar."
  [{:keys [sender-name]}]
  ($ :div {:style {:position "relative" :flexShrink 0}}
    ($ :div {:style {:width 44 :height 44 :borderRadius "50%"
                     :background "var(--mantine-color-gray-2)"
                     :color "var(--mantine-color-gray-7)"
                     :display "flex" :alignItems "center" :justifyContent "center"
                     :fontWeight 600 :fontSize 14}}
      (inbox/initials sender-name))
    ($ :div {:style {:position "absolute" :bottom -2 :right -2
                     :width 18 :height 18 :borderRadius "50%"
                     :background "#1877f2" :color "white"
                     :fontSize 10 :fontWeight 700
                     :display "flex" :alignItems "center" :justifyContent "center"
                     :border "2px solid white"}}
      "f")))

(defui conversation-row
  "One inbox row. Whole row is a link to the existing thread view.
   unread-count > 0 (SMS-29): bold sender name, tinted row background, and
   a trailing dot — per the mockup's .b-item.unread / .b-unread."
  [{:keys [conversation now]}]
  (let [{:keys [id sender-name last-message-at unread-count
                last-message-preview last-message-direction]} conversation
        preview (inbox/preview-text last-message-preview last-message-direction)
        time (inbox/relative-time last-message-at now)
        unread? (pos? (or unread-count 0))]
    ($ ui/anchor
      {:href (router/href :conversation/thread {:id id})
       :underline "never"
       :c "inherit"
       :style {:display "flex" :gap 10 :alignItems "flex-start"
               :width "100%" :padding "12px 16px" :textAlign "left"
               :borderBottom "1px solid var(--mantine-color-gray-1)"
               :background (when unread? "var(--mantine-color-blue-0)")}}
      ($ conversation-avatar {:sender-name sender-name})
      ($ :div {:style {:flex 1 :minWidth 0}}
        ($ ui/text {:size "sm" :fw (if unread? 700 500) :truncate true}
          (or sender-name "Unknown"))
        ;; Facebook-style: message preview and relative time share one line,
        ;; joined by a "·" separator, instead of the time being right-aligned.
        ($ ui/text {:size "xs" :c "dimmed" :truncate true :mt 1}
          preview
          (when (and preview time) " · ")
          time))
      (when unread?
        ($ :div {:style {:width 9 :height 9 :borderRadius "50%"
                         :background "var(--mantine-color-blue-6)"
                         :flexShrink 0 :alignSelf "center"}})))))

(defui conversation-list
  [{:keys [conversations loading? now]}]
  (cond
    (and loading? (empty? conversations))
    ($ ui/center {:py "xl"} ($ ui/loader))

    (empty? conversations)
    ($ ui/stack {:align "center" :gap "xs" :py "xl"}
      ($ icons/icon-message-circle {:size 48 :stroke 1.5
                                    :color "var(--mantine-color-gray-4)"})
      ($ ui/text {:size "lg" :fw 500 :c "dimmed"}
        "No conversations yet")
      ($ ui/text {:size "sm" :c "dimmed" :ta "center"}
        "New messages will appear here."))

    :else
    ($ ui/stack {:gap 0}
      (for [c conversations]
        ($ conversation-row {:key (:id c) :conversation c :now now})))))

;; ============================================================================
;; Page (container)
;; ============================================================================

(def ^:private poll-interval-ms
  "SMS-29: keeps the list (and each row's unread state) current while the
   Inbox page is open — mirrors conversation_thread.cljs's poll-interval-ms."
  5000)

(defui inbox-page
  "Fetches conversations via the shared aggregate list event (recency sort is
   the API default) and renders them as Option-B rows. Polls on an interval
   while mounted (SMS-29) so unread state and recency stay current."
  []
  (let [conversations (urf/use-subscribe [::agg.subs/all :conversation])
        loading? (urf/use-subscribe [::agg.subs/loading? :conversation])
        ;; `now` is read once at render for the relative-time labels and passed
        ;; down — the formatter itself stays clock-free (see lib inbox ns).
        now (js/Date.)]
    (use-effect
     (fn []
       (rf/dispatch [::agg.events/list :conversation])
       (let [timer (js/setInterval
                    #(rf/dispatch [::agg.events/list :conversation])
                    poll-interval-ms)]
         (fn [] (js/clearInterval timer))))
     [])
    ($ ui/stack {:gap "md"}
      ($ ui/title {:order 2 :style {:fontFamily "Sora, sans-serif"}}
        "Conversations")
      ($ ui/paper {:withBorder true :shadow "xs" :radius "md"
                   :style {:overflow "hidden"}}
        ($ conversation-list
          {:conversations conversations
           :loading? loading?
           :now now})))))
